import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Star, CheckCircle2, Sparkles, MapPin, GraduationCap, Search, MessageSquare, Phone as PhoneIcon, Handshake, Home as HomeIcon, Utensils, BookOpen, Briefcase, PartyPopper, Wallet, Trophy, Award, FileText, Gift, Network, Lock } from "lucide-react";
import Navbar from "@/components/campus/Navbar";
import Footer from "@/components/campus/Footer";
import Countdown from "@/components/campus/Countdown";
import Typewriter from "@/components/campus/Typewriter";
import { api } from "@/lib/api";

const fadeUp = { initial:{opacity:0,y:30}, whileInView:{opacity:1,y:0}, viewport:{once:true,margin:"-50px"}, transition:{duration:0.6} };
const stagger = { initial:{opacity:0,y:30}, whileInView:{opacity:1,y:0}, viewport:{once:true} };

export default function Landing() {
  const [colleges, setColleges] = useState([]);

  useEffect(() => { api.listColleges().then(setColleges).catch(()=>{}); }, []);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Navbar/>

      {/* HERO */}
      <section className="relative min-h-screen pt-24 pb-16 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 dots-bg opacity-60 pointer-events-none"/>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#2563EB]/15 blur-[120px] rounded-full pointer-events-none"/>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#1E3A8A]/20 blur-[120px] rounded-full pointer-events-none"/>

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.5}} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"/> Launch Offer Active — Ends 26 July 2026
            </motion.div>
            <motion.h1 initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:0.7,delay:0.1}} className="font-display font-bold mt-6 leading-[1.05] tracking-tight text-5xl md:text-7xl">
              Ask Someone Who<br/>
              <span className="bg-gradient-to-r from-[#2563EB] to-[#60A5FA] bg-clip-text text-transparent">Actually Lives There.</span>
            </motion.h1>

            <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.3}} className="mt-7">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Students are asking right now —</div>
              <div className="glass rounded-2xl px-5 py-4 flex items-center gap-3 max-w-xl">
                <MessageSquare className="text-[#2563EB] shrink-0" size={20}/>
                <div className="font-display text-lg md:text-xl text-white"><Typewriter/></div>
              </div>
            </motion.div>

            <p className="mt-6 text-slate-300 text-base md:text-lg max-w-xl leading-relaxed">
              Connect with verified Campus Ambassadors at any college. Get honest answers about campus life, placements, hostel, and more.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/colleges" data-testid="hero-find-college" className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-semibold hover:bg-amber-400 transition glow-amber">
                Find Your College <ArrowRight className="group-hover:translate-x-1 transition" size={18}/>
              </Link>
              <a href="#ambassador" data-testid="hero-become-ambassador" className="px-6 py-3 rounded-full border border-white/15 text-white hover:bg-white/5 transition">
                Become an Ambassador
              </a>
            </div>

            <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-3 gap-4 max-w-lg">
              <Stat n="7+" l="Colleges"/>
              <Stat n="FREE" l="During Launch"/>
              <Stat n="100%" l="Verified Only"/>
            </div>
          </div>

          {/* Chat Mockup */}
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <motion.div initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:0.7,delay:0.2}} className="glass rounded-3xl p-5 float-1 relative">
                <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] flex items-center justify-center text-white font-bold">RK</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold flex items-center gap-1.5">Rahul K. <CheckCircle2 size={14} className="text-emerald-400"/></div>
                    <div className="text-xs text-slate-400">KIIT · CSE · 3rd Year</div>
                  </div>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Verified</span>
                </div>

                <div className="space-y-3 py-4 text-sm">
                  <Bubble side="right">How's the hostel at KIIT actually?</Bubble>
                  <Bubble side="left">I'm in Kalinga hostel — 2 sharing, AC rooms available. Food is decent, not amazing. Wi-Fi works fine in rooms.</Bubble>
                  <Bubble side="right">What about placements?</Bubble>
                  <Bubble side="left">CSE is strong — Microsoft, Infosys visited this year. Avg ~6.5 LPA. CGPA matters a lot here.</Bubble>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <span className="text-[11px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-semibold">FREE during launch</span>
                  <div className="flex items-center gap-2 text-xs text-slate-400"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"/>Online now</div>
                </div>
              </motion.div>

              <div className="absolute -top-6 -right-6 bg-white text-[#0F172A] px-3 py-1.5 rounded-full shadow-lg text-xs font-semibold flex items-center gap-1 rotate-3 float-2">
                <Star size={14} className="fill-amber-400 text-amber-400"/> 4.8 Rating
              </div>
              <div className="absolute -bottom-4 -left-6 bg-[#F59E0B] text-[#0F172A] px-3 py-1.5 rounded-full shadow-lg text-xs font-bold float-3 -rotate-2">
                Chat FREE →
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COUNTDOWN STRIP */}
      <section className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-[#0F172A]">
            <div className="text-xs font-bold tracking-[0.25em] uppercase opacity-70">Launch offer ends in:</div>
            <div className="font-display font-bold text-2xl md:text-3xl">All offers expire on 26 July 2026</div>
          </div>
          <Countdown/>
        </div>
      </section>

      {/* PROBLEM */}
      <section id="problem" className="py-24 px-6 md:px-12 bg-white text-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#2563EB]">The Real Problem</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 tracking-tight">The college brochure is never the whole story.</h2>
            <p className="text-[#64748B] mt-4 max-w-2xl mx-auto">Every student goes through the same broken research cycle. Does this sound familiar?</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            <ProblemCard icon="🔍" color="red" title="Google gives 2-year-old articles" body="SEO content written by people who've never set foot on campus. Feels like reading a sales pitch." tag="Sound familiar?"/>
            <ProblemCard icon="📞" color="orange" title="Counsellors earn from your admission" body="They say '100% placement, beautiful campus' — but they earn commission. Everything is filtered." tag="Never objective."/>
            <ProblemCard icon="📱" color="purple" title="Instagram DMs go unanswered" body="You send 10 messages to student vloggers. One replies with two lines. Real conversation almost never happens." tag="Broken process."/>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 px-6 md:px-12 bg-[#F0F7FF] text-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#2563EB]">How It Works</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3">Real answers in three simple steps</h2>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-6">
            <StepCard icon={<GraduationCap/>} title="Search your target college" body="Browse verified Campus Ambassadors — name, branch, year, rating." amber={false}/>
            <StepCard icon={<MessageSquare/>} title="Start a chat or book a call" body="Chat free or book a 10-minute audio call. Your details stay private." amber={false}/>
            <StepCard icon={<CheckCircle2/>} title="Get honest, real answers" body="Ask about hostel, faculty, placements, fees, campus life — anything." amber={false}/>
            <StepCard icon={<Handshake/>} title="We handle your admission — free" body="Click 'I've Decided' and our team manages your entire admission at no cost." amber/>
          </div>
        </div>
      </section>

      {/* COLLEGES */}
      <section className="py-24 px-6 md:px-12 bg-white text-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#2563EB]">Our Colleges</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3">Find ambassadors at your college</h2>
            <p className="text-[#64748B] mt-3 max-w-2xl mx-auto">Every college below has verified Campus Ambassadors ready to answer your questions.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {colleges.slice(0, 6).map((c) => (
              <Link key={c.slug} to={`/college/${c.slug}`} data-testid={`college-card-${c.slug}`} className="group bg-[#F0F7FF] border border-[#DBEAFE] rounded-2xl p-5 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#2563EB]/20 hover:border-[#2563EB]/40 transition-all">
                <div className="flex gap-4">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] text-white flex items-center justify-center font-display font-bold text-2xl shrink-0">{c.name.split(" ").map(w=>w[0]).slice(0,2).join("")}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg text-[#0F172A] truncate">{c.name}</div>
                    <div className="text-sm text-[#64748B] flex items-center gap-1 mt-0.5"><MapPin size={12}/>{c.city}, {c.state}</div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {c.naac_grade && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">NAAC {c.naac_grade}</span>}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold capitalize">{c.college_type}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(c.streams || []).slice(0,3).map((s) => (
                    <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#DBEAFE] text-[#64748B]">{s}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-[#64748B]">{c.ambassador_count || 0} Campus Ambassadors</span>
                  <span className="text-[#2563EB] font-semibold group-hover:translate-x-1 transition inline-flex items-center gap-1">Explore <ArrowRight size={14}/></span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/colleges" data-testid="view-all-colleges" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#2563EB] text-[#2563EB] font-semibold hover:bg-[#2563EB] hover:text-white transition">View All Colleges <ArrowRight size={16}/></Link>
          </div>
        </div>
      </section>

      {/* WHAT YOU CAN ASK */}
      <section className="py-24 px-6 md:px-12 bg-[#F0F7FF] text-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl font-bold">No question is too small. Ask everything.</h2>
            <p className="text-[#64748B] mt-3">Campus Ambassadors have lived it — the real experience, not the brochure.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Topic icon={<HomeIcon/>} title="Hostel & Accommodation" body="Room sharing, cleanliness, security, distance from class, warden rules"/>
            <Topic icon={<Utensils/>} title="Mess & Canteen" body="Food quality, timings, options, outside food availability"/>
            <Topic icon={<BookOpen/>} title="Faculty & Teaching" body="Teacher quality, approachability, strictness, best and worst departments"/>
            <Topic icon={<Briefcase/>} title="Placements & Companies" body="Real companies, actual packages, CGPA requirements, preparation tips"/>
            <Topic icon={<PartyPopper/>} title="Campus Life & Culture" body="Fests, clubs, sports, social life, overall campus vibe"/>
            <Topic icon={<Wallet/>} title="Fees & Hidden Costs" body="Full fee breakdown, surprise charges, scholarship reality, installments"/>
          </div>
        </div>
      </section>

      {/* OFFERS */}
      <section id="offers" className="py-24 px-6 md:px-12 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0 dots-bg opacity-30"/>
        <div className="relative max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-10">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-amber-400">Launch Offers</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3">Join before 26 July 2026</h2>
            <p className="text-slate-400 mt-3">Admission season is live. These offers won't come back after the deadline.</p>
          </motion.div>
          <div className="flex justify-center mb-10"><Countdown dark/></div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="glass rounded-3xl p-7 border-[#2563EB]/40">
              <div className="text-xs font-bold tracking-widest uppercase text-[#60A5FA]">For Students</div>
              <h3 className="font-display text-2xl font-bold mt-2">Talk to anyone, anywhere</h3>
              <div className="mt-5 space-y-3">
                <OfferRow service="Chat" original="₹49" final="FREE" code="FIRSTCOLLEGE"/>
                <OfferRow service="Audio Call" original="₹99" final="₹49.50" code="FIRSTCALL"/>
              </div>
              <p className="text-xs text-slate-400 mt-4">Valid once per college per service. Expires 26 July 2026.</p>
              <Link to="/colleges" className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-blue-600 transition">Use Offer Now <ArrowRight size={16}/></Link>
            </div>
            <div className="glass rounded-3xl p-7 border-amber-500/40">
              <div className="text-xs font-bold tracking-widest uppercase text-amber-400">For Campus Ambassadors</div>
              <h3 className="font-display text-2xl font-bold mt-2">Become a Founding Ambassador</h3>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                <li className="flex gap-2"><Sparkles className="text-amber-400 shrink-0 mt-0.5" size={16}/>Founding Ambassador badge (permanent gold)</li>
                <li className="flex gap-2"><Sparkles className="text-amber-400 shrink-0 mt-0.5" size={16}/>Special edition founding certificate</li>
                <li className="flex gap-2"><Sparkles className="text-amber-400 shrink-0 mt-0.5" size={16}/>Priority in ambassador leaderboard</li>
                <li className="flex gap-2"><Sparkles className="text-amber-400 shrink-0 mt-0.5" size={16}/>First batch of exclusive goodies kit</li>
              </ul>
              <Link to="/auth?mode=register" className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-[#F59E0B] text-[#0F172A] font-semibold hover:bg-amber-400 transition">Apply Before Deadline <ArrowRight size={16}/></Link>
            </div>
          </div>
        </div>
      </section>

      {/* AMBASSADOR */}
      <section id="ambassador" className="py-24 px-6 md:px-12 bg-gradient-to-b from-[#0F172A] to-[#0a1124]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#60A5FA]">For Campus Ambassadors</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 leading-[1.1]">Be the guide<br/>you never had.</h2>
            <p className="text-slate-400 mt-5 leading-relaxed">You've lived the campus experience. Help thousands make the most important academic decision of their life — and build your profile while doing it.</p>

            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              <Perk icon={<FileText/>} title="Official Certificate" body="LinkedIn-ready Campus Ambassadorship certificate"/>
              <Perk icon={<Gift/>} title="Goodies Kit" body="Physical goodies mailed to top ambassadors"/>
              <Perk icon={<Briefcase/>} title="CV Boost" body="Real leadership role that recruiters notice"/>
              <Perk icon={<Handshake/>} title="Internship Referrals" body="Priority access to internships from our network"/>
              <Perk icon={<Trophy/>} title="Top Ambassador Badge" body="Monthly leaderboard visible on your profile"/>
              <Perk icon={<Network/>} title="Lifelong Network" body="Connect with ambassadors across India"/>
            </div>

            <div className="glass rounded-2xl p-5 mt-6 border-amber-500/30">
              <div className="flex items-center gap-3">
                <Lock className="text-amber-400" size={20}/>
                <div className="flex-1">
                  <div className="font-semibold">Complete 30 audio calls (lifetime)</div>
                  <div className="text-xs text-slate-400">→ Unlock direct earnings per call · Payments go straight to your bank</div>
                </div>
              </div>
              <div className="mt-3 h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-amber-400 rounded-full" style={{width:"0%"}}/></div>
              <div className="text-xs text-slate-400 mt-2">No monthly target — take your time</div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="glass rounded-3xl p-7">
              <div className="flex text-amber-400 gap-1 mb-3">{[1,2,3,4,5].map(i=><Star key={i} size={16} className="fill-amber-400"/>)}</div>
              <p className="text-slate-200 text-lg leading-relaxed">"I joined as a Campus Ambassador and helped over 40 students in one semester. My LinkedIn had something real to show — not just grades. The certificate opened doors I didn't expect."</p>
              <div className="mt-4 text-sm text-slate-400">— CSE · 3rd Year · KIIT Bhubaneswar</div>
            </div>
            <Link to="/auth?mode=register" data-testid="apply-ambassador-cta" className="block text-center px-6 py-4 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold hover:bg-amber-400 transition glow-amber">
              Apply as Campus Ambassador →
            </Link>
            <div className="text-xs text-slate-400 text-center">College ID required for verification · Takes 5 minutes to apply</div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-6 md:px-12 bg-[#F0F7FF] text-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#2563EB]">What Students Say</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3">Real conversations. Real decisions.</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            <Quote text="I almost paid 4 lakhs for a college based on Insta reels. One 10-min call here told me what nobody else did. Saved my year." who="PCM · Bihar"/>
            <Quote text="The senior I spoke to was brutally honest about which branch had real placements. Picked CSE over IT because of that call." who="PCB · Karnataka"/>
            <Quote text="Decided in one chat. The Campus Senior team filled my forms and tracked deadlines. I just had to show up." who="Commerce · Maharashtra"/>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">The right college starts with the right conversation.</h2>
          <p className="text-blue-100 mt-4 text-lg">Stop guessing. Talk to someone who actually lives there.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/colleges" className="px-6 py-3 rounded-full bg-white text-[#2563EB] font-bold hover:bg-blue-50 transition">Find My College</Link>
            <Link to="/auth?mode=register" className="px-6 py-3 rounded-full border-2 border-white text-white hover:bg-white/10 transition">Become an Ambassador</Link>
          </div>
        </div>
      </section>

      <Footer/>
    </div>
  );
}

const Stat = ({n,l}) => (<div><div className="font-display font-bold text-2xl text-white">{n}</div><div className="text-xs text-slate-400 mt-0.5">{l}</div></div>);
const Bubble = ({side, children}) => (
  <div className={`flex ${side==="right"?"justify-end":"justify-start"}`}>
    <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl ${side==="right"?"bg-[#2563EB] text-white rounded-br-sm":"bg-white/10 text-white rounded-bl-sm border border-white/10"}`}>{children}</div>
  </div>
);
const ProblemCard = ({icon,color,title,body,tag}) => {
  const colors = { red:"border-l-red-500 bg-red-50 text-red-700", orange:"border-l-orange-500 bg-orange-50 text-orange-700", purple:"border-l-purple-500 bg-purple-50 text-purple-700" };
  return (
    <motion.div {...stagger} className={`bg-white rounded-2xl p-6 border border-slate-100 border-l-4 ${color==="red"?"border-l-red-500":color==="orange"?"border-l-orange-500":"border-l-purple-500"} hover:shadow-xl transition`}>
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-display font-bold text-lg">{title}</h3>
      <p className="text-[#64748B] mt-2 text-sm leading-relaxed">{body}</p>
      <span className={`inline-block text-[10px] mt-4 px-2 py-1 rounded-full font-semibold ${colors[color]}`}>{tag}</span>
    </motion.div>
  );
};
const StepCard = ({icon,title,body,amber}) => (
  <motion.div {...stagger} className={`rounded-2xl p-6 transition hover:-translate-y-1 ${amber?"bg-[#F59E0B] text-[#0F172A] glow-amber":"bg-white border border-[#DBEAFE]"}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${amber?"bg-[#0F172A] text-amber-400":"bg-[#2563EB] text-white"}`}>{icon}</div>
    <h3 className="font-display font-bold text-lg">{title}</h3>
    <p className={`mt-2 text-sm leading-relaxed ${amber?"text-[#0F172A]/80":"text-[#64748B]"}`}>{body}</p>
  </motion.div>
);
const Topic = ({icon,title,body}) => (
  <motion.div {...stagger} className="bg-white border border-[#DBEAFE] rounded-2xl p-5 hover:-translate-y-1 hover:shadow-lg transition">
    <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center mb-3">{icon}</div>
    <h3 className="font-display font-bold">{title}</h3>
    <p className="text-[#64748B] text-sm mt-1.5">{body}</p>
  </motion.div>
);
const Perk = ({icon,title,body}) => (
  <div className="glass rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg bg-[#2563EB]/15 text-[#60A5FA] flex items-center justify-center mb-2">{icon}</div>
    <div className="font-semibold text-white text-sm">{title}</div>
    <div className="text-xs text-slate-400 mt-1">{body}</div>
  </div>
);
const Quote = ({text, who}) => (
  <motion.div {...stagger} className="bg-white rounded-2xl p-6 border border-[#DBEAFE]">
    <div className="flex gap-1 text-amber-400 mb-3">{[1,2,3,4,5].map(i=><Star key={i} size={14} className="fill-amber-400"/>)}</div>
    <p className="text-[#0F172A] leading-relaxed">"{text}"</p>
    <div className="text-xs text-[#64748B] mt-3">— {who}</div>
  </motion.div>
);
const OfferRow = ({service, original, final, code}) => (
  <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
    <div>
      <div className="text-sm text-slate-400">{service}</div>
      <div className="flex items-baseline gap-2 mt-0.5"><span className="line-through text-slate-500">{original}</span><span className="font-display font-bold text-lg text-emerald-400">{final}</span></div>
    </div>
    <code className="text-xs px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 font-mono">{code}</code>
  </div>
);
